import React, { ChangeEvent, HTMLInputTypeAttribute } from 'react'
import clsx from 'clsx'
import { FieldHookConfig, useField, useFormikContext } from 'formik'
import { SizeVariants } from '@/shared/forms/components/constants'
import NcMarkdown from '@/shared/components/NcMarkdown'
import { AnimatePresence, motion } from 'framer-motion'

export type Option<T> = {
  id: T
  label: string
  disclaimer?: string
  disabled?: string
}

export type FormikRadioProps<T> = {
  label: string
  size: keyof typeof SizeVariants
  autocomplete: string
  options: Option<T>[]
  disabled?: boolean
  placeholder?: string
  optional?: boolean
  type?: HTMLInputTypeAttribute

  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
} & FieldHookConfig<T>

export default function FormikRadio<T>({
  label,
  size,
  options,
  autocomplete,
  disabled,
  placeholder,

  optional = false,
  ...props
}: FormikRadioProps<T>) {
  const [field, meta, helpers] = useField<T>(props)
  const { isSubmitting } = useFormikContext()
  const isDisabled = disabled || isSubmitting
  const typedOptions = options as Array<Option<T>>
  const selected = typedOptions.find((option) => option.id === field.value)

  return (
    <div className={SizeVariants[size as keyof typeof SizeVariants]}>
      <div className="flex justify-between">
        <label
          htmlFor={props.id}
          className={clsx(
            'block text-sm font-medium leading-6',
            isDisabled ? 'text-gray-500' : 'text-gray-900',
          )}
        >
          <NcMarkdown>{label}</NcMarkdown>
        </label>
        {optional && (
          <span className="text-sm leading-6 text-gray-500" id="email-optional">
            Optional
          </span>
        )}
      </div>
      <div className="mt-2 space-y-3">
        {typedOptions.map(({ id, label, disabled }) => {
          const optionIsDisabled =
            (disabled !== undefined && disabled !== null) || isDisabled

          return (
            <div
              key={`${props.name}-${id}`}
              className="group relative flex items-center gap-x-3"
            >
              {disabled && (
                <span
                  className="absolute left-24 m-4 mx-auto rounded-md bg-gray-800
    px-2.5 py-1 text-sm text-gray-100 opacity-0 transition-opacity group-hover:opacity-100"
                >
                  {disabled}
                </span>
              )}

              <input
                id={`${props.name}-${id}`}
                name={props.name}
                type="radio"
                className={clsx(
                  'h-4 w-4 border-gray-300 focus:ring-blue-600',
                  optionIsDisabled ? 'text-slate-300' : 'text-blue-600',
                )}
                onChange={(ev) => {
                  helpers
                    .setValue(id, false)
                    .then(() => helpers.setTouched(true, true).then())

                  if (props.onChange) props.onChange(ev)
                }}
                onBlur={field.onBlur}
                disabled={optionIsDisabled}
                checked={field.value === id}
                value={`${id}`}
              />
              <label
                htmlFor={`${props.name}-${id}`}
                className={clsx(
                  'block text-sm font-medium leading-6',
                  optionIsDisabled ? 'text-gray-500' : 'text-gray-900',
                )}
              >
                {label}
              </label>
            </div>
          )
        })}
        {meta.touched && meta.error && (
          <div className="mt-2 text-sm font-semibold text-red-600">
            {meta.error}
          </div>
        )}
        <AnimatePresence mode="sync">
          {selected?.disclaimer && (
            <motion.p
              className="mt-2 text-sm font-semibold text-gray-600"
              initial={{ x: '-3%', opacity: 0 }}
              animate={{ x: '0%', opacity: 1 }}
              exit={{ x: '3%', opacity: 0, height: 0 }}
              transition={{ ease: 'easeInOut', duration: 0.3 }}
            >
              {selected.disclaimer}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
